// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import type { SearchRequest } from '@medplum/core';
import { formatSearchQuery, getReferenceString } from '@medplum/core';
import type { Communication } from '@medplum/fhirtypes';
import { useMedplumProfile } from '@medplum/react';
import type { ThreadBucket } from '@polarissleep/messaging';
import { normalizeCommunicationSearch } from '@polarissleep/messaging';
import type { JSX } from 'react';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { ThreadInbox } from '../../components/messages/ThreadInbox';
import classes from './MessagesPage.module.css';

export function MessagesPage(): JSX.Element {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useMedplumProfile();

  const recipientFilter = profile ? getReferenceString(profile) : undefined;

  const currentSearch = useMemo(() => (location.search ? location.search.substring(1) : ''), [location.search]);

  const { normalizedSearch, parsedSearch, bucket } = useMemo(
    () =>
      normalizeCommunicationSearch({
        search: currentSearch,
      }),
    [currentSearch]
  );

  useEffect(() => {
    const isDetailView = Boolean(threadId);
    if (!isDetailView && normalizedSearch !== currentSearch) {
      const prefix = normalizedSearch ? `?${normalizedSearch}` : '';
      navigate(`/messages${prefix}`, { replace: true })?.catch(console.error);
    }
  }, [currentSearch, navigate, normalizedSearch, threadId]);

  const onChange = (search: SearchRequest): void => {
    const fhirQuery = formatSearchQuery(search);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    navigate(`/messages${fhirQuery}${sep}bucket=${bucket}`)?.catch(console.error);
  };

  const getThreadUri = (topic: Communication): string => {
    const fhirQuery = formatSearchQuery(parsedSearch);
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/messages/${topic.id}${fhirQuery}${sep}bucket=${bucket}`;
  };

  const buildBucketUri = (value: ThreadBucket): string => {
    const fhirQuery = formatSearchQuery({ ...parsedSearch, offset: 0 });
    const sep = fhirQuery.startsWith('?') ? '&' : '?';
    return `/messages${fhirQuery}${sep}bucket=${value}`;
  };

  const unreadUri = buildBucketUri('unread');
  const sentUri = buildBucketUri('sent');
  const allUri = buildBucketUri('all');

  return (
    <div className={classes.container}>
      <ThreadInbox
        threadId={threadId}
        query={formatSearchQuery(parsedSearch).substring(1)}
        bucket={bucket}
        getThreadUri={getThreadUri}
        onChange={onChange}
        unreadUri={unreadUri}
        sentUri={sentUri}
        allUri={allUri}
        recipientFilter={recipientFilter}
      />
    </div>
  );
}
